import { useEffect, useMemo, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import PageHeader from '../../../components/owner/PageHeader';
import { usePanelToast } from '../../../context/PanelToastContext';
import { usePlatformSettings } from '../../../context/PlatformSettingsContext';
import { formatPlatformPrice } from '../../../utils/platformMoney';
import GymSubscriptionForm from './GymSubscriptionForm';
import {
  fetchGymSubscriptions,
  fetchSaasPlansForSelect,
  createGymSubscription,
  updateGymSubscription,
  deleteGymSubscription,
} from '../../../services/owner/subscriptionsApi';
import { peekOwnerCache } from '../../../services/cache/ownerCache';
import {
  exportSubscriptionCsv,
  exportSubscriptionExcel,
  exportSubscriptionPdf,
  exportSubscriptions,
  printSubscriptionList,
} from './exportSubscriptions';
import '../ownerPages.css';
import '../subscriptions/subscriptions.css';

function StatusBadge({ status }) {
  const normalized = (status || '').toLowerCase();
  const badgeClass =
    normalized === 'active'
      ? 'owner-badge-success'
      : normalized === 'trial'
        ? 'owner-badge-info'
        : normalized === 'expired'
          ? 'owner-badge-warning'
          : 'owner-badge-info';

  const label =
    normalized === 'trial'
      ? 'Free Trial'
      : status
        ? status.charAt(0).toUpperCase() + status.slice(1)
        : '—';

  return (
    <span className={`owner-badge ${badgeClass}`}>
      {label}
    </span>
  );
}

function SourceBadge({ source }) {
  const isSignup = source === 'signup';
  return (
    <span className={`owner-badge ${isSignup ? 'owner-badge-info' : 'owner-badge-success'}`}>
      {isSignup ? 'Signup' : 'Manual'}
    </span>
  );
}

function SubscriptionManagement() {
  const toast = usePanelToast();
  const { formatPrice = formatPlatformPrice } = usePlatformSettings();
  const cachedSubs = peekOwnerCache('gym-subscriptions');
  const cachedPlans = peekOwnerCache('saas-plans');
  const [subscriptionList, setSubscriptionList] = useState(cachedSubs || []);
  const [plans, setPlans] = useState(cachedPlans || []);
  const [loading, setLoading] = useState(!cachedSubs);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    const [subs, planList] = await Promise.all([
      fetchGymSubscriptions(),
      fetchSaasPlansForSelect(),
    ]);
    setSubscriptionList(subs);
    setPlans(planList);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!peekOwnerCache('gym-subscriptions')) setLoading(true);
        setError('');
        await loadData();
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load subscriptions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredSubscriptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    return subscriptionList.filter((sub) => {
      const matchesSearch =
        !query ||
        sub.gym.toLowerCase().includes(query) ||
        sub.planName.toLowerCase().includes(query) ||
        sub.billingCycle.toLowerCase().includes(query) ||
        sub.status.toLowerCase().includes(query) ||
        (sub.contactEmail || '').toLowerCase().includes(query) ||
        (sub.source || '').toLowerCase().includes(query);
      const matchesSource = sourceFilter === 'all' || sub.source === sourceFilter;
      return matchesSearch && matchesSource;
    });
  }, [subscriptionList, search, sourceFilter]);

  const openCreateModal = () => {
    setFormKey((k) => k + 1);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => setShowCreateModal(false);

  const handleCreateSubmit = async (form) => {
    try {
      setSaving(true);
      const created = await createGymSubscription(form, plans);
      setSubscriptionList((prev) => [created, ...prev]);
      closeCreateModal();
      toast.showSaved('Subscription');
    } catch (err) {
      toast.showError(err.message || 'Failed to create subscription');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (sub) => {
    setFormKey((k) => k + 1);
    setEditTarget(sub);
  };

  const closeEditModal = () => setEditTarget(null);

  const editInitialData = editTarget
    ? {
        gym: editTarget.gym,
        gymId: editTarget.gymId,
        planId: editTarget.planId,
        billingCycle: editTarget.billingCycle,
        price: editTarget.price,
        startDate: editTarget.startDate,
        expiryDate: editTarget.expiryDate,
        status: editTarget.status,
        contactEmail: editTarget.contactEmail || '',
      }
    : null;

  const handleEditSubmit = async (form) => {
    try {
      setSaving(true);
      const updated = await updateGymSubscription(editTarget.id, form, plans);
      setSubscriptionList((prev) => prev.map((sub) => (sub.id === editTarget.id ? updated : sub)));
      closeEditModal();
      toast.showSaved('Subscription');
    } catch (err) {
      toast.showError(err.message || 'Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setSaving(true);
      await deleteGymSubscription(deleteTarget.id);
      setSubscriptionList((prev) => prev.filter((sub) => sub.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.showDeleted('Subscription');
    } catch (err) {
      toast.showError(err.message || 'Failed to delete subscription');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Gym Subscriptions"
        subtitle="Manual subscriptions and gym signups appear here automatically"
      >
        <button type="button" className="owner-btn owner-btn-primary" onClick={openCreateModal}>
          Create Subscription
        </button>
      </PageHeader>

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading subscriptions...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16" style={{ color: 'var(--owner-danger, #dc2626)' }}>{error}</p>}

      <div className="rec-member-toolbar admin-list-toolbar">
        <div className="rec-member-search-field">
          <MdSearch className="rec-member-search-icon" />
          <input
            type="text"
            className="owner-form-input rec-member-search-input"
            placeholder="Search subscriptions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="owner-form-input rec-member-toolbar-select"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          aria-label="Filter by source"
        >
          <option value="all">All Sources</option>
          <option value="manual">Manual</option>
          <option value="signup">Signup</option>
        </select>
        <div className="rec-member-toolbar-actions">
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => printSubscriptionList(filteredSubscriptions)}>
            Print
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportSubscriptionCsv(filteredSubscriptions)}>
            Export CSV
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportSubscriptionPdf(filteredSubscriptions)}>
            Export PDF
          </button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={() => exportSubscriptionExcel(filteredSubscriptions)}>
            Export Excel
          </button>
          <button type="button" className="owner-btn owner-btn-primary owner-btn-sm" onClick={() => exportSubscriptions(filteredSubscriptions)}>
            Export
          </button>
        </div>
      </div>

      <div className="owner-card rec-panel-card owner-panel-section">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table owner-panel-table">
            <thead>
              <tr>
                <th>Gym</th>
                <th>Plan</th>
                <th>Billing Cycle</th>
                <th>Price</th>
                <th>Expiry Date</th>
                <th>Source</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscriptions.length > 0 ? (
                filteredSubscriptions.map((sub) => (
                  <tr key={sub.id}>
                    <td>
                      <div>{sub.gym}</div>
                      {sub.contactEmail && (
                        <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{sub.contactEmail}</div>
                      )}
                    </td>
                    <td>
                      <div>{sub.planName || '—'}</div>
                      {(sub.isTrial || sub.status === 'trial') && (
                        <div style={{ marginTop: 4 }}>
                          <span className="owner-badge owner-badge-info">Free Trial</span>
                        </div>
                      )}
                    </td>
                    <td>{sub.billingCycle || '—'}</td>
                    <td>{formatPrice(Number(sub.price) || 0)}</td>
                    <td>{sub.expiryDate}</td>
                    <td><SourceBadge source={sub.source || 'manual'} /></td>
                    <td><StatusBadge status={sub.status} /></td>
                    <td>
                      <div className="rec-member-actions-cell">
                        <button
                          type="button"
                          className="owner-btn owner-btn-secondary owner-btn-sm"
                          onClick={() => openEditModal(sub)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="owner-btn owner-btn-sm rec-member-btn-delete"
                          onClick={() => setDeleteTarget(sub)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '24px' }}>
                    {loading ? 'Loading...' : 'No subscriptions found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCreateModal && (
        <div className="subscription-modal-overlay" onClick={closeCreateModal}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="create-subscription-title">
            <div className="subscription-modal-header">
              <h2 id="create-subscription-title" className="subscription-modal-title">Create Subscription (Manual)</h2>
              <button type="button" className="subscription-modal-close" onClick={closeCreateModal} aria-label="Close">×</button>
            </div>
            <GymSubscriptionForm
              key={formKey}
              plans={plans}
              submitLabel={saving ? 'Creating…' : 'Create Subscription'}
              onSubmit={handleCreateSubmit}
              onCancel={closeCreateModal}
            />
          </div>
        </div>
      )}

      {editTarget && (
        <div className="subscription-modal-overlay" onClick={closeEditModal}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="edit-subscription-title">
            <div className="subscription-modal-header">
              <h2 id="edit-subscription-title" className="subscription-modal-title">Edit Subscription</h2>
              <button type="button" className="subscription-modal-close" onClick={closeEditModal} aria-label="Close">×</button>
            </div>
            <GymSubscriptionForm
              key={formKey}
              plans={plans}
              initialData={editInitialData}
              submitLabel={saving ? 'Saving…' : 'Save Changes'}
              onSubmit={handleEditSubmit}
              onCancel={closeEditModal}
            />
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="subscription-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Subscription</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <p className="rec-member-modal-message">
              Delete subscription for <strong>{deleteTarget.gym}</strong> ({deleteTarget.planName})? This action cannot be undone.
            </p>
            <div className="subscription-form-actions">
              <button type="button" className="owner-btn rec-member-btn-delete" onClick={handleDelete} disabled={saving}>
                {saving ? 'Deleting…' : 'Delete'}
              </button>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubscriptionManagement;
